import { useEffect, useRef } from 'react';

import { PanelScopeProvider } from '../contexts/PanelScopeContext';
import { usePanelNavigation } from '../stores/usePanelNavigation';
import { usePanelPreferenceStore } from '../stores/usePanelPreferenceStore';
import { usePanelScopeRegistry } from '../stores/usePanelScopeRegistry';
import { useSelectionChannel } from '../stores/useSelectionChannel';

import type { SelectionData } from '../stores/useSelectionChannel';

/**
 * ListScopeProvider Props
 */
interface ListScopeProviderProps {
  /**
   * 자식 컴포넌트
   */
  children: React.ReactNode;

  /**
   * Scope 키
   */
  scopeKey: string;
}

/**
 * List Scope를 초기화하고 SelectionChannel → PanelNavigation으로 연결합니다.
 * @description
 * - Registry에서 scope 설정을 검증하고 selection을 구독합니다.
 * - selection 발생 시 viewMode='standalone'으로 detail 패널을 엽니다.
 * - 언마운트 시 resetScope/clearSelection으로 정리합니다.
 * - **Phase 4-B**: 멀티 선택 지원 추가, 레거시 브릿지 제거 완료
 * @example
 * ```tsx
 * <ListScopeProvider scopeKey="dns/zone/list">
 *   <ZoneDataTable />
 * </ListScopeProvider>
 * ```
 */
export default function ListScopeProvider({ scopeKey, children }: ListScopeProviderProps) {
  const { subscribeSelection, clearSelection } = useSelectionChannel();
  const { openBottomPanel, closePanel, resetScope } = usePanelNavigation();

  // Registry에서 scopeKey에 해당하는 config를 구독 (등록 시 리렌더링 트리거)
  const config = usePanelScopeRegistry(state => state.registry[scopeKey]);

  // V2 패널 환경설정: list 패널 활성화 여부 확인
  // useRef로 관리하여 subscription callback에서 최신 값 참조 (의존성 배열에서 제외)
  const listEnabled = usePanelPreferenceStore(state => state.preferences.list.enabled);
  const listAvailable = usePanelPreferenceStore(state => state.availability.list);
  const panelStateRef = useRef({ listEnabled, listAvailable });

  // 최신 값 동기화 (렌더링마다 업데이트)
  panelStateRef.current = { listEnabled, listAvailable };

  // config를 ref로 관리하여 subscription callback에서 최신 값 참조
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    // 1. config가 없으면 아직 scope가 등록되지 않은 상태
    // (useRegisterPanelScopes와의 타이밍 이슈 방지 - config 구독으로 등록 시 재실행됨)
    if (!config) {
      return;
    }

    // 2. SelectionChannel 구독
    const unsubscribe = subscribeSelection(scopeKey, (selection: SelectionData | null) => {
      // selection이 클리어되면 패널 닫기
      if (!selection) {
        closePanel(scopeKey);
        return;
      }

      // 패널이 비활성화되어 있으면 열지 않음 (ref에서 최신 값 참조)
      const { listEnabled: enabled, listAvailable: available } = panelStateRef.current;
      if (!enabled || !available) {
        return;
      }

      // config가 없으면 무시 (ref에서 최신 값 참조)
      const currentConfig = configRef.current;
      if (!currentConfig) {
        return;
      }

      // Phase 4-B: 다중 선택 지원
      // - mode === 'multi': 배열로 전달
      // - mode === 'single': 첫 번째 ID만 전달
      const panelId = selection.mode === 'multi' ? selection.ids : selection.ids[0];

      // 3. Provider가 viewMode 결정 (항상 'standalone')
      // 4. Registry에서 panelResource 조회
      // Phase 6: openDetailPanel → openBottomPanel 리네이밍
      openBottomPanel({
        scopeKey,
        panelResource: currentConfig.panelResource,
        panelId,
        viewMode: 'standalone',
      });
    });

    // 5. cleanup (subscription만 해제, selection은 유지)
    return () => {
      unsubscribe();
    };
  }, [scopeKey, config, subscribeSelection, openBottomPanel, closePanel]);

  // 언마운트 시에만 정리 (scopeKey 변경 시에도 실행되지 않도록 ref 사용)
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;

  useEffect(() => {
    // GNB 비활성화나 좁은 뷰포트로 사용할 수 없게 되면 이미 열린 패널 닫음.
    if (listEnabled && listAvailable) {
      return;
    }

    closePanel(scopeKey);
  }, [closePanel, listAvailable, listEnabled, scopeKey]);

  useEffect(() => {
    return () => {
      resetScope(scopeKeyRef.current);
      clearSelection(scopeKeyRef.current);
    };
  }, [resetScope, clearSelection]);

  return <PanelScopeProvider scopeKey={scopeKey}>{children}</PanelScopeProvider>;
}
